import { useState, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../lib/apiClient';
import { LogoMark } from './LogoMark';

/**
 * Login-time 2FA challenge. The backend (FirebaseAuthGuard) refuses every route for an account
 * with 2FA enabled until `POST /auth/2fa/verify` accepts a code; that route takes either the
 * 6-digit authenticator code or one of the 8-character backup codes (TwoFactorService.verifyCode).
 * The portal is English-only today, so every string lives in TWO_FACTOR_TEXT, ready to translate.
 * Codes are held in component state only until submit: never persisted, never logged.
 */
const TWO_FACTOR_TEXT = {
  title: 'Two-factor verification',
  subtitleTotp: 'Enter the 6-digit code from your authenticator app to continue.',
  subtitleBackup: 'Enter one of your 8-character backup codes. Each backup code works once.',
  labelTotp: 'Authentication code',
  labelBackup: 'Backup code',
  submit: 'Verify',
  submitting: 'Verifying…',
  useBackup: 'Use a backup code instead',
  useTotp: 'Use my authenticator app instead',
  signOut: 'Sign out',
  invalidFormatTotp: 'Enter the 6-digit code from your authenticator app.',
  invalidFormatBackup: 'Enter your 8-character backup code (letters A-F and digits 0-9).',
  invalidCode: 'That code is not valid. Check the code and try again.',
  tooManyRequests: 'Too many attempts. Wait a minute, then try again.',
  locked: 'Too many failed attempts. This account is temporarily locked; try again later.',
  generic: 'We could not verify the code. Check your connection and try again.',
} as const;

type Mode = 'totp' | 'backup';

/** Strip whitespace/dashes pasted from an authenticator app or a printed backup-code sheet. */
const normalize = (raw: string, mode: Mode): string => {
  const compact = raw.replace(/[\s-]/g, '');
  return mode === 'backup' ? compact.toUpperCase() : compact;
};

const isWellFormed = (code: string, mode: Mode): boolean =>
  mode === 'totp' ? /^\d{6}$/.test(code) : /^[0-9A-F]{8}$/.test(code);

const describeVerifyError = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.statusCode === 429) return TWO_FACTOR_TEXT.tooManyRequests;
    if (err.statusCode === 400) {
      // The backend reports lockout as a 400 whose message mentions the lock.
      return /locked|too many/i.test(err.message) ? TWO_FACTOR_TEXT.locked : TWO_FACTOR_TEXT.invalidCode;
    }
  }
  return TWO_FACTOR_TEXT.generic;
};

export function TwoFactorChallenge() {
  const { verifyTwoFactor, signOut } = useAuth();
  const [mode, setMode] = useState<Mode>('totp');
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchMode = () => {
    setMode((m) => (m === 'totp' ? 'backup' : 'totp'));
    setValue('');
    setError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const code = normalize(value, mode);
    if (!isWellFormed(code, mode)) {
      setError(mode === 'totp' ? TWO_FACTOR_TEXT.invalidFormatTotp : TWO_FACTOR_TEXT.invalidFormatBackup);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await verifyTwoFactor(code);
      // On success the auth status flips and this component unmounts.
    } catch (err) {
      setError(describeVerifyError(err));
      setValue('');
      setSubmitting(false);
    }
  };

  const label = mode === 'totp' ? TWO_FACTOR_TEXT.labelTotp : TWO_FACTOR_TEXT.labelBackup;

  return (
    <div className="login-screen">
      <div className="login-hero">
        <div className="login-hero-mark" aria-hidden="true">
          <LogoMark size={480} />
        </div>
        <div className="login-hero-content">
          <LogoMark size={40} />
          <h1>One more step before the console opens.</h1>
          <p>Admin accounts are protected with a second factor.</p>
        </div>
        <div className="login-hero-foot">© {new Date().getFullYear()} MedicLynk. Internal use only.</div>
      </div>

      <div className="login-panel">
        <form className="login-panel-inner" onSubmit={handleSubmit} noValidate>
          <LogoMark size={36} />
          <h2>{TWO_FACTOR_TEXT.title}</h2>
          <p className="auth-subtitle">
            {mode === 'totp' ? TWO_FACTOR_TEXT.subtitleTotp : TWO_FACTOR_TEXT.subtitleBackup}
          </p>

          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}

          <label htmlFor="two-factor-code" className="tfa-label">
            {label}
          </label>
          <input
            id="two-factor-code"
            className="form-control tfa-code-input"
            type="text"
            inputMode={mode === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            autoCapitalize={mode === 'backup' ? 'characters' : 'off'}
            autoCorrect="off"
            spellCheck={false}
            maxLength={mode === 'totp' ? 12 : 16}
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={submitting}
            aria-invalid={error ? true : undefined}
          />

          <button type="submit" className="btn btn-primary tfa-submit" disabled={submitting}>
            {submitting ? TWO_FACTOR_TEXT.submitting : TWO_FACTOR_TEXT.submit}
          </button>

          <div className="tfa-links">
            <button type="button" className="tfa-link" onClick={switchMode} disabled={submitting}>
              {mode === 'totp' ? TWO_FACTOR_TEXT.useBackup : TWO_FACTOR_TEXT.useTotp}
            </button>
            <button type="button" className="tfa-link" onClick={() => void signOut()}>
              {TWO_FACTOR_TEXT.signOut}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
